<template>
    <div class="container">
        <v-title :lineage="[{'Transactions':'transactions'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Dispute for {{ titleId | truncate({dots: true})}}
            <div slot="actions">
                <v-cancel :disabled="isLoading"
                          :defaultRoute="{name: 'transaction-details', params: {id: transactionId}}"></v-cancel>
                <r-button type="primary" :disabled="isLoading" @click="save">Save Dispute</r-button>
            </div>
        </v-title>
        <v-error-messages
            :has-frontend-errors="$v.dispute.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.dispute.status.required">Dispute status is required</li>
            <li class="list-item" v-if="!$v.dispute.reasonCode.required">Dispute reason code is required</li>
            <template>
                <li class="list-item" v-if="!$v.dispute.postedTime.isEmpty">Dispute posted time is required</li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Dispute Information</h2>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Type"
                                :options="disputeTypes"
                                v-model="dispute.type"
                                />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Status"
                                :options="disputeStatus"
                                v-model="dispute.status"
                                :validate="$v.dispute.status"
                                />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-date-input
                                label="Posted Time"
                                type="datetime"
                                v-model="disputedPostedTime"
                                :validate="$v.dispute.postedTime"
                                />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-date-input
                                label="Deadline Time"
                                type="datetime"
                                v-model="disputedDeadlineTime"
                                />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-select
                                label="Currency"
                                :options="currencies"
                                v-model="dispute.currency"
                                />
                        </div>
                        <div class="grid-item grid-item-quarter grid-item-break-half">
                            <r-input
                                label="Amount"
                                :left-label="dispute.currency | currencySign"
                                v-model="dispute.amount"
                                />
                        </div>
                        <div class="grid-item grid-item-full">
                            <v-select
                                label="Dispute Reason Code"
                                :options="reasonOptions"
                                v-model="dispute.reasonCode"
                                :validate="$v.dispute.reasonCode"
                            />
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Acquirer Reference Number"
                                v-model="dispute.acquirerReferenceNumber"
                                />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :disabled="isLoading"
                          :defaultRoute="{name: 'transaction-details', params: {id: transactionId}}"></v-cancel>
                <r-button type="primary" :disabled="isLoading" @click="save">Save Dispute</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        vCancel, vDateInput, vErrorMessages, vFormTile, vIconButton, vSelect,
    } from '@/components/form';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import {required} from 'vuelidate/lib/validators';
    import DisputeModel from '@/common/models/dispute-model';
    import DISPUTES_MAP from '@/data/disputes-map';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import vTitle from '@/components/v-title';

    export default {
        name: 'dispute-form',
        mixins: [FormLoaderMixin],
        props: {
            transactionId: {
                type: [Number, String],
                default: null,
            },
        },
        components: {
            vTitle,
            vFormTile,
            vCancel,
            vIconButton,
            vSelect,
            vErrorMessages,
            vDateInput,
        },
        data() {
            return {
                dispute: new DisputeModel({transactionId: this.transactionId}),
                disputeTypes: DISPUTES_MAP.types,
                disputeReasons: DISPUTES_MAP.reasons,
                disputeStatus: DISPUTES_MAP.status,
                currencies: CURRENCIES_LIST,
                paymentCardBrand: null,
            };
        },
        validations: {
            dispute: {
                status: {required},
                postedTime: {required},
                reasonCode: {required},
            },
        },
        computed: {
            titleId() {
                return this.transactionId || this.dispute.id;
            },
            isDataRequired() {
                return this.isFormUpdating || !this.isDataReady;
            },
            reasonOptions() {
                return this.disputeReasons.filter(item => item.scheme === this.paymentCardBrand).map(item => ({label: `${item.reasonCode} - ${item.description}`, value: item.reasonCode}));
            },
            disputedPostedTime: {
                get() {
                    return this.dispute.postedTime && this.$tz().readUTC(this.dispute.postedTime);
                },
                set(value) {
                    this.dispute.postedTime = value.isValid()
                        ? value.format()
                        : null;
                },
            },
            disputedDeadlineTime: {
                get() {
                    return this.dispute.deadlineTime && this.$tz().readUTC(this.dispute.deadlineTime);
                },
                set(value) {
                    this.dispute.deadlineTime = value.isValid()
                        ? value.format()
                        : null;
                },
            },
        },
        methods: {
            async save() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    const params = {
                        data: this.dispute.getPayload(),
                    };
                    if (this.isFormUpdating) {
                        params.id = this.dispute.id;
                    }
                    const action = this.isFormUpdating ? 'update' : 'create';
                    this.isLoading = true;
                    try {
                        await this.$api(({disputes}) => disputes[action](params));
                        this.$toast.positive(`Dispute successfully ${action}d`);
                        if (!this.isFormUpdating) {
                            this.$router.push({name: 'transaction-details', params: {id: this.transactionId}});
                        }
                    } catch (error) {
                        this.$toast.fromError(error, `Dispute could not be ${action}d`);
                        this.isLoading = false;
                        this.serverError = error;
                    } finally {
                        window.scrollTo(0, 0);
                        this.isLoading = false;
                    }
                }
            },
            async getTransaction(id, callback = null) {
                const params = {
                    id,
                    expand: 'paymentCard',
                };
                const {fields} = await this.$api(({transactions}) => transactions.get(params));
                this.paymentCardBrand = fields.paymentCard.brand;
                if (callback) {
                    callback(fields);
                }
            },
            async fetchData() {
                try {
                    if (this.isFormUpdating) {
                        const {fields} = await this.$api(({disputes}) => disputes.get({id: this.id}));
                        await this.getTransaction(fields.transactionId);
                        this.dispute = new DisputeModel(fields);
                    } else {
                        await this.getTransaction(this.transactionId, (fields) => {
                            this.dispute.amount = fields.amount;
                            this.dispute.currency = fields.currency;
                        });
                    }
                } catch (error) {
                    this.$toast.fromError(error);
                }
            },
        },
    };
</script>
